import React from "react";
import {ThemeProvider} from "@mui/material/styles";
import {ThemeProvider as ScThemeProvider} from "styled-components";
import {AccountAppColorContext} from "account/brand/AccountAppColorContext";
import {useAccountAppColor} from "account/hooks/useAccountAppColor";
import {useThemeMode} from "./ThemeModeProvider";
import {themeFor} from "./theme";

/**
 * Themes the app with the signed-in account's brand colour (kitecorp/transferify-ui#2935).
 *
 * Sits inside `UserProvider`, which `AppTheme` wraps, so it re-provides the MUI and styled-components
 * themes rather than replacing `AppTheme`'s. The providers are ALWAYS rendered, with the plain theme when
 * there is no colour: switching between a wrapped and an unwrapped tree would remount the whole app when
 * the colour arrives, and with it any transfer in progress.
 *
 * The colour only ever arrives after mount (it is fetched once the auth listener confirms the session), so
 * the first client render is the unbranded theme and matches the pre-rendered HTML.
 */
export function AccountBrandTheme({children}: {children: React.ReactNode}) {
	const {mode} = useThemeMode();
	const {color, publish} = useAccountAppColor();
	const theme = themeFor(mode, color);
	return (
		<AccountAppColorContext.Provider value={publish}>
			<ThemeProvider theme={theme}>
				<ScThemeProvider theme={theme}>
					{children}
				</ScThemeProvider>
			</ThemeProvider>
		</AccountAppColorContext.Provider>
	);
}
